import { useMemo } from 'react';
import { useBetslipContext, BetslipEvent } from '../../../../contexts/BetslipContext';
import { BetWithMarketOutcomeEvent, MarketType } from '../../../../@types';
import { areMarketsCombinable } from '../../../../helpers';

export type GolaBetslipRow =
  | {
      kind: 'betBuilder';
      bet: BetslipEvent;
    }
  | {
      kind: 'group';
      bets: BetslipEvent[];
    };

type GroupableBet = BetslipEvent | BetWithMarketOutcomeEvent;

type GroupingConfig<T extends GroupableBet> = {
  getEventId: (bet: T) => string;
  getMarketType: (bet: T) => MarketType;
  getBetId: (bet: T) => string;
};

const getBetslipEventConfig = (): GroupingConfig<BetslipEvent> => ({
  getEventId: (bet) => bet.eventId,
  getMarketType: (bet) => bet.marketType,
  getBetId: (bet) => bet.outcomeId,
});

const getHistoryBetConfig = (): GroupingConfig<BetWithMarketOutcomeEvent> => ({
  getEventId: (bet) => bet.event.id,
  getMarketType: (bet) => bet.market.marketType,
  getBetId: (bet) => bet.id,
});

const groupBets = <T extends GroupableBet>(bets: T[] | undefined, config: GroupingConfig<T>): T[][] => {
  if (!bets || bets.length <= 1) {
    return bets?.map((bet) => [bet]) ?? [];
  }

  const betsByEvent = bets.reduce(
    (acc: Record<string, T[]>, bet: T) => {
      const eventId = config.getEventId(bet);
      if (!acc[eventId]) {
        acc[eventId] = [];
      }
      acc[eventId].push(bet);
      return acc;
    },
    {} as Record<string, T[]>
  );

  const result: T[][] = [];

  Object.values(betsByEvent).forEach((eventBets: T[]) => {
    if (eventBets.length === 1) {
      result.push(eventBets);
      return;
    }

    const nonCombinableBets: T[] = [];

    eventBets.forEach((currentBet: T) => {
      const isCombinable = eventBets.some(
        (otherBet) =>
          config.getBetId(otherBet) !== config.getBetId(currentBet) &&
          areMarketsCombinable(config.getMarketType(currentBet), config.getMarketType(otherBet))
      );

      if (isCombinable) {
        result.push([currentBet]);
      } else {
        nonCombinableBets.push(currentBet);
      }
    });

    if (nonCombinableBets.length > 0) {
      result.push(nonCombinableBets);
    }
  });

  return result;
};

export const useGroupedBetslipItems = (): GolaBetslipRow[] => {
  const { bets } = useBetslipContext();
  const config = useMemo(() => getBetslipEventConfig(), []);

  return useMemo(() => {
    const normalBets = bets.filter((bet) => !bet.betBuilder);
    const groupedNormalBets = groupBets(normalBets, config);
    const normalBetGroupByOutcomeId = new Map<string, BetslipEvent[]>();
    const handledGroups = new Set<BetslipEvent[]>();

    groupedNormalBets.forEach((betGroup) => {
      betGroup.forEach((bet) => {
        normalBetGroupByOutcomeId.set(bet.outcomeId, betGroup);
      });
    });

    return bets.flatMap<GolaBetslipRow>((bet) => {
      if (bet.betBuilder) {
        return [{ kind: 'betBuilder' as const, bet }];
      }

      const betGroup = normalBetGroupByOutcomeId.get(bet.outcomeId);

      if (!betGroup || handledGroups.has(betGroup)) {
        return [];
      }

      handledGroups.add(betGroup);

      return [{ kind: 'group' as const, bets: betGroup }];
    });
  }, [bets, config]);
};

export const useGroupedHistoryBets = (bets: BetWithMarketOutcomeEvent[] | undefined) => {
  const config = useMemo(() => getHistoryBetConfig(), []);

  return useMemo(() => groupBets(bets, config), [bets, config]);
};
